/**
 * The match-events log — PD-4, the split architecture's doorbell view.
 *
 * Modelled on `components/notify/NotifyLogTable.tsx`, replay button included,
 * because it replaces it: on a split pebble the submit doorbell is the WRITE of
 * `/match_events/requests/{pebbleId}_{storyVersion}`, not a `notify_log` POST.
 * The two coexist on the pages that show both — a legacy-sourced pebble still
 * rings the old doorbell and produces no node (PB-8), so a page that dropped
 * `NotifyLogTable` would go blank for exactly the pebbles that still use it.
 *
 * ## Every row is a way out
 *
 * The acceptance is "from a match-events row the match, story and refinement
 * pages open". The links are computed in `model.ts` (`RowLinks`) rather than
 * assembled here, so they are assertable without a DOM, and a link is rendered
 * ONLY when its target exists: a request node has a story link only once the
 * ledger says which story it produced, and a result node has a refinement link
 * only when its payload carries both a session and a match. A link that lands
 * nowhere is worse than no link on a page whose job is to be trusted.
 *
 * ## Replay
 *
 * `POST /admin/match-events/:requestKey/replay` re-posts the STORED request
 * node to the matcher's own ingest — the same door PB-9's sweep re-fires a
 * stuck node through. The ingest's ledger decides what happens, and this table
 * reports that decision verbatim rather than colouring it:
 *
 *   202 duplicate   already finished; nothing ran. The normal answer.
 *   409             a live claim or a re-claimable failure; the node stays
 *                   `pending` for the sweep. Informative, not an error.
 *   202 accepted    the request had never completed, and now has.
 *
 * Only a request row with a stored node is replayable: a result node is not a
 * request, and a ledger row whose node PB-9 has GC'd has nothing to re-post.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { replayMatchEvent } from '../../api/client';
import { Badge, RawJson } from '../conversation/ui';
import {
  buildRows,
  formatStamp,
  toneFor,
  type MatchEventRow,
  type MatchEventsLog,
} from './model';

interface Props {
  log: MatchEventsLog | null;
  /** Called after a replay so the page can re-read the log. */
  onAfterReplay?: () => void;
  /** Highlight (and open) one node, by key. */
  highlightKey?: string | null;
  /**
   * Show only rows with this label — a result `kind` or a request status. This
   * is how `RefinementSessionCard` opens "the relevant node": a session row
   * cannot derive its own result key (§ that file), so the link filters by
   * `kind=questions_ready` instead of addressing a key it would have to guess.
   */
  filterLabel?: string | null;
  /** Rendered above the table when there is nothing to show. */
  emptyLabel?: string;
}

export default function MatchEventsTable({
  log,
  onAfterReplay,
  highlightKey,
  filterLabel,
  emptyLabel,
}: Props) {
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<{ key: string; text: string } | null>(null);

  const all = buildRows(log);
  // Filtered, never hidden: the banner below says how many rows the filter is
  // withholding, because a log that silently shows a subset is a log that lies.
  const rows = filterLabel ? all.filter((r) => r.label === filterLabel) : all;

  async function handleReplay(row: MatchEventRow) {
    if (
      !confirm(
        `Re-fire ${row.key} through the matcher ingest?\n\n` +
          'This is the sweep path: if the request already finished the ingest answers ' +
          'duplicate and nothing runs.',
      )
    ) {
      return;
    }
    setPendingKey(row.key);
    setError(null);
    setOutcome(null);
    try {
      const result = await replayMatchEvent(row.key);
      setOutcome({ key: row.key, text: describeReplay(result) });
      onAfterReplay?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPendingKey(null);
    }
  }

  const issues = Object.entries(log?.sources ?? {}).filter(
    ([, s]) => s.status === 'error' || s.status === 'not_configured',
  );

  return (
    <div className="space-y-2">
      {issues.length > 0 && (
        <div
          className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded p-2"
          data-testid="match-events-sources"
        >
          {issues.map(([name, s]) => (
            <div key={name}>
              <span className="font-medium">{name}</span>: {s.status}
              {s.error ? ` — ${s.error}` : ''}
              {s.detail ? ` (${s.detail})` : ''}
            </div>
          ))}
        </div>
      )}

      {filterLabel && (
        <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded p-2" data-testid="match-events-filter">
          showing <span className="font-medium">{filterLabel}</span> only — {rows.length} of {all.length} rows
        </div>
      )}

      {rows.length === 0 ? (
        <div className="card text-sm text-gray-500">
          {filterLabel
            ? `No ${filterLabel} rows in this window.`
            : (emptyLabel ?? 'No match events yet.')}
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-4">Time</th>
                <th className="py-2 pr-4">Node</th>
                <th className="py-2 pr-4">What</th>
                <th className="py-2 pr-4">Ingest</th>
                <th className="py-2 pr-4">Opens</th>
                <th className="py-2 pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <Row
                  key={row.id}
                  row={row}
                  highlighted={highlightKey != null && highlightKey === row.key}
                  busy={pendingKey !== null}
                  pending={pendingKey === row.key}
                  outcome={outcome?.key === row.key ? outcome.text : null}
                  onReplay={() => handleReplay(row)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

function Row({
  row,
  highlighted,
  busy,
  pending,
  outcome,
  onReplay,
}: {
  row: MatchEventRow;
  highlighted: boolean;
  busy: boolean;
  pending: boolean;
  outcome: string | null;
  onReplay: () => void;
}) {
  return (
    <tr
      data-testid="match-event-row"
      data-node-key={row.key}
      className={`border-b border-gray-100 align-top ${highlighted ? 'bg-amber-50' : ''}`}
    >
      <td className="py-2 pr-4 whitespace-nowrap text-xs text-gray-700">{formatStamp(row.ts)}</td>

      <td className="py-2 pr-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge tone={row.side === 'request' ? 'purple' : 'blue'}>
            {row.side === 'request' ? 'submit → matcher' : 'matcher → pebble'}
          </Badge>
          {row.nodeMissing && (
            <Badge tone="gray" title="the RTDB node has been garbage-collected; this is the D1 ledger row">
              node gc&apos;d
            </Badge>
          )}
        </div>
        <div className="mt-1 text-xs text-gray-500 font-mono break-all">{row.key}</div>
        {row.requestKey && row.side === 'result' && (
          <div className="text-xs text-gray-400 font-mono break-all">← {row.requestKey}</div>
        )}
      </td>

      <td className="py-2 pr-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge tone={toneFor(row)}>{row.label}</Badge>
          {row.storyVersion !== null && <span className="text-xs text-gray-600">v{row.storyVersion}</span>}
          {row.source && <Badge tone="gray">{row.source}</Badge>}
          {row.clientEvent && <Badge tone="gray">{row.clientEvent}</Badge>}
          {row.mustTellUser === true && <Badge tone="amber">must tell user</Badge>}
          {row.origin && <Badge tone="gray" title={row.originId ?? undefined}>{row.origin}</Badge>}
          {row.attempts !== null && row.attempts > 1 && <Badge tone="amber">attempt {row.attempts}</Badge>}
        </div>
        {row.noChangeReason && (
          <div className="mt-1 text-xs text-gray-500">reason: {row.noChangeReason}</div>
        )}
        {row.error && <div className="mt-1 text-xs text-red-700 break-all">{row.error}</div>}
      </td>

      <td className="py-2 pr-4 text-xs text-gray-700">
        {row.ledger === null ? (
          <span className="text-gray-400">—</span>
        ) : (
          <div className="space-y-0.5">
            <div>{row.ledger.outcome ?? '—'}</div>
            {row.ledger.latencyMs !== null && <div className="text-gray-500">{row.ledger.latencyMs} ms</div>}
            {row.ledger.bestScore !== null && (
              <div className="text-gray-500">best {row.ledger.bestScore.toFixed(3)}</div>
            )}
          </div>
        )}
      </td>

      <td className="py-2 pr-4">
        <div className="flex flex-col gap-0.5 text-xs">
          <RowLink to={row.links.timeline} label="timeline" />
          <RowLink to={row.links.story} label="story" />
          <RowLink to={row.links.match} label="match" />
          <RowLink to={row.links.refinement} label="refinement" />
        </div>
      </td>

      <td className="py-2 pr-4">
        {row.replayable ? (
          <button
            type="button"
            className="px-2 py-1 text-xs rounded bg-pebbl-600 text-white hover:bg-pebbl-700 disabled:opacity-50"
            onClick={onReplay}
            disabled={busy}
          >
            {pending ? '…' : 'Replay'}
          </button>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
        {outcome && <div className="mt-1 text-xs text-gray-700 max-w-xs">{outcome}</div>}
        <div className="mt-1 space-y-1">
          {row.extracted && (
            <RawJson
              value={row.extracted}
              label={`extracted (${Object.keys(row.extracted).length})${row.extractedWasRtdbKeyed ? ' — decoded' : ''}`}
              open={highlighted}
            />
          )}
          {row.payload && <RawJson value={row.payload} label="payload" open={highlighted} />}
          {!row.nodeMissing && <RawJson value={row.node} label="raw node (as stored)" open={highlighted} />}
          {row.ledger && <RawJson value={row.ledger} label="ledger row" />}
        </div>
      </td>
    </tr>
  );
}

function RowLink({ to, label }: { to: string | null; label: string }) {
  if (to === null) return null;
  return (
    <Link to={to} className="text-pebbl-600 hover:text-pebbl-800">
      {label} →
    </Link>
  );
}

/**
 * The ingest's own answer, in one sentence. Deliberately descriptive rather
 * than judgemental: a 409 is the correct outcome of replaying a node that is
 * still in flight, and painting it red would teach operators to distrust a
 * button that is working.
 */
export function describeReplay(result: {
  ingest_status?: number;
  ingest?: unknown;
}): string {
  const status = result.ingest_status ?? 0;
  const body = (result.ingest ?? {}) as Record<string, unknown>;
  if (status === 202 && body.duplicate === true) {
    return `already ${String(body.outcome ?? 'finished')} — nothing re-ran (202 duplicate)`;
  }
  if (status === 202) {
    return `ingested: ${String(body.outcome ?? 'accepted')} (202)`;
  }
  if (status === 409) {
    return body.in_progress === true
      ? 'a claim is still live — the node stays pending for the sweep (409)'
      : `not finished (${String(body.ledger_outcome ?? 'unknown')}) — the node stays pending (409)`;
  }
  return `ingest answered ${status}${body.error ? `: ${String(body.error)}` : ''}`;
}
